import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip, statusTone } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { fmtEur, fmtDate, euro } from '../../lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ContactDetailMobile } from './ContactDetailMobile';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { leggibile } from '../../components/mobile/MobileKit';
import { SERVICE_STATUS_LABEL } from '@masamune/shared';
import {
  canWriteContatti, contattoQuery, numeroWhatsApp, TIPO_LABEL,
  type ContattoCompleto, type TrattativaDTO,
} from './contactsApi';
import './contacts-desktop.css';

/**
 * La scheda di un contatto: la catena intera in una colonna sola.
 *
 * L'ordine e' quello con cui si guarda un soggetto prima di chiamarlo: chi e'
 * e come si raggiunge, cosa stiamo trattando, cosa gli stiamo facendo, cosa
 * paga ogni mese, cosa ci deve. E se ci vende anche lui, le due facce insieme
 * con il saldo netto: su Tridentum Auto sono 71.250 di fatture ricevute contro
 * tre emesse, e guardarne una sola racconta il contrario di quello che e'
 * successo.
 *
 * I totali delle fatture arrivano dal server, mai sommati nel browser dalle
 * ultime caricate per la tabella. Fatturato e incassato guardano TUTTE le
 * fatture; scaduto e da incassare sono il residuo delle rate delle fatture
 * emesse dalla data di taglio dell'Amministrazione (`creditiClienti`, 7/10/2026).
 */
/**
 * Sul telefono la scheda è ContactDetailMobile (come un contatto dell'iPhone:
 * gesti rapidi, poi solo le righe che hanno un dato). Sul computer resta questa.
 */
export function ContactDetailPage() {
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const isMobile = useIsMobile();
  if (!kind || !id) return <Navigate to="/crm" replace />;
  return isMobile ? <div className="page"><ContactDetailMobile kind={kind} id={id} /></div> : <ContactDetailDesktop />;
}

function ContactDetailDesktop() {
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteContatti(user);

  const { data: c, isLoading, isError } = useQuery({
    ...contattoQuery(kind ?? '', id ?? ''),
    enabled: !!kind && !!id,
    retry: false,
  });

  const promuovi = useMutation({
    mutationFn: () => api.post<{ key: string; trattativeCollegate?: number }>('/contacts/promuovi', { kind, id }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      toast.ok('Ora è un cliente', r.trattativeCollegate ? `${r.trattativeCollegate} trattative collegate alla scheda` : undefined);
      navigate(`/contatti/${r.key}`, { replace: true });
    },
    onError: (e: any) => toast.alert('Non promosso', e?.message ?? 'Riprova'),
  });

  if (!kind || !id) return <Navigate to="/crm" replace />;
  if (isError) {
    return (
      <div className="page">
        <Breadcrumb items={[{ label: 'Contatti', to: '/crm' }, { label: 'Scheda' }]} />
        <div className="max-w-[480px] mx-auto py-20 text-center">
          <h1 className="t-h1">Contatto non trovato</h1>
          <div className="mute mt-2">Forse è stato eliminato, o il link è sbagliato.</div>
        </div>
      </div>
    );
  }
  // header="chips" e non "avatar": questa scheda l'avatar non ce l'ha, e lo
  // scheletro con il cerchio da 56px spostava tutto il blocco del titolo di 68px
  // verso destra — poi, all'arrivo dei dati, tornava indietro. Lo scheletro
  // serve proprio a non far saltare niente.
  if (isLoading || !c) return <DetailSkeleton header="chips" />;

  const a = c.anagrafica;
  const aperte = c.trattative.filter((t) => t.outcome === 'APERTO');
  const chiuse = c.trattative.filter((t) => t.outcome !== 'APERTO');
  const potenziale = c.tipi.includes('POTENZIALE');
  // Quanto entra ogni mese: i contratti scritti in Amministrazione piu' gli
  // abbonamenti riportati a mese.
  const mensile = c.canoneMensile + c.mensileAbbonamenti;
  // Se l'azienda non ha una mail sua ma un referente ce l'ha, si mostra quella
  // dicendo di chi e': meglio "scrivi a Giuliano" che "non ce l'abbiamo".
  const ripiego = c.referenti.find((r) => r.email) ?? c.referenti.find((r) => r.phone) ?? null;

  return (
    // m-detail: sotto i 768px la scheda e' una colonna sola (mobile.md §7).
    // Sul computer la classe non dichiara niente.
    <div className="page m-detail">
      <Breadcrumb items={[{ label: 'Contatti', to: '/crm' }, { label: a.shortName }]} />

      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px] ct2-det-hd">
          <BrandLogo name={leggibile(c.name)} size={56} className="ct2-det-logo" />
          <div className="min-w-0">
          <h1 className="ed-title truncate">{leggibile(c.name)}</h1>
          {/* Solo i pezzi che esistono: su un potenziale appena nato non c'e'
              niente da scrivere qui, e una riga di trattini fa sembrare rotta
              la scheda. */}
          <div className="detail-meta mt-2 truncate">
            {a.vat && <span className="mono">{a.vat}</span>}
            {a.vat && (a.city || a.sector) && <span className="mute-2">·</span>}
            {(a.city || a.sector) && <span>{[a.city, a.sector].filter(Boolean).join(' · ')}</span>}
            {a.sinceYear && <><span className="mute-2">·</span><span>dal {a.sinceYear}</span></>}
            {a.accountManager && (
              <>
                <span className="mute-2">·</span>
                <span><span className="mute-2 mr-1.5">Resp.</span><span className="v">{a.accountManager.firstName[0]}. {a.accountManager.lastName}</span></span>
              </>
            )}
          </div>
          {/* Le etichette: un soggetto puo' essere piu' cose insieme, ed e'
              esattamente il punto per cui questa sezione esiste. */}
          {/* Cos'è, in parole (prima tre pastiglie): un soggetto può essere più cose insieme. */}
          <div className="ct2-det-cosa">
            {c.tipi.map((t) => TIPO_LABEL[t]).join(' e ')}
            {a.status !== 'ATTIVO' && a.status !== 'LEAD' && <span className="mute"> · {a.status.replace('_', ' ').toLowerCase()}</span>}
          </div>
          </div>
        </div>
        {/* d-only: sul telefono queste azioni scendono nella barra in fondo,
            dove arriva il pollice (mobile.md §4). I gesti rapidi (chiama,
            scrivi, WhatsApp) stanno qui accanto, come nella rubrica. */}
        <div className="flex gap-2 pb-1 shrink-0 d-only items-center">
          {(() => {
            const telefono = c.recapiti.phone ?? ripiego?.phone ?? null;
            const mail = c.recapiti.email ?? ripiego?.email ?? null;
            const w = numeroWhatsApp(telefono);
            const gesti: Array<{ icon: 'phone' | 'mail' | 'message'; label: string; href: string | null; esterno?: boolean }> = [
              { icon: 'phone', label: 'Chiama', href: telefono ? `tel:${telefono.replace(/\s+/g, '')}` : null },
              { icon: 'mail', label: 'Scrivi una mail', href: mail ? `mailto:${mail}` : null },
              { icon: 'message', label: 'Scrivi su WhatsApp', href: w ? `https://wa.me/${w}` : null, esterno: true },
            ];
            return gesti.map((g) => g.href
              ? <a key={g.icon} className="btn btn-icon" href={g.href} title={g.label} aria-label={g.label} {...(g.esterno ? { target: '_blank', rel: 'noopener noreferrer' } : {})}><Icon name={g.icon} size={15} /></a>
              : <span key={g.icon} className="btn btn-icon ct2-det-off" title={`${g.label}: manca il recapito`} aria-hidden="true"><Icon name={g.icon} size={15} /></span>);
          })()}
          {canWrite && potenziale && (
            <button className="btn btn-primary" disabled={promuovi.isPending} onClick={() => promuovi.mutate()}>
              <Icon name="check" size={12} /> Diventa cliente
            </button>
          )}
          {c.kind === 'client' && (
            <button className="btn" onClick={() => navigate(`/clients/${c.id}`)}>
              Scheda estesa
            </button>
          )}
        </div>
      </div>

      {/* Come sta il rapporto, in una frase coi numeri dentro (prima erano
          quattro riquadri: fatturato, scaduto, ogni mese, potenziale). Ogni
          numero compare una volta: sotto, le sezioni non lo ripetono. */}
      <p className="ct2-det-frase">
        {(() => {
          const pezzi: React.ReactNode[] = [];
          if (c.fatture.count > 0) pezzi.push(<>gli abbiamo fatturato <strong>{euro(c.fatture.totale)}</strong> in {c.fatture.count} {c.fatture.count === 1 ? 'fattura' : 'fatture'}{c.fatture.annullate > 0 ? ` (più ${c.fatture.annullate} annullate)` : ''}</>);
          if (mensile > 0) pezzi.push(<>paga <strong>{euro(mensile)}</strong> al mese</>);
          if (c.fatture.scaduto > 0) pezzi.push(<>ci deve <strong className="ct2-det-alert">{euro(c.fatture.scaduto)}</strong> già scaduti</>);
          else if (c.fatture.daIncassare > 0) pezzi.push(<>aspettiamo <strong>{euro(c.fatture.daIncassare)}</strong> non ancora scaduti</>);
          else if (c.fatture.count > 0) pezzi.push(<>non ci deve niente</>);
          if (c.potenziale > 0) pezzi.push(<>ha {aperte.length === 1 ? 'una trattativa aperta' : `${aperte.length} trattative aperte`} per <strong>{euro(c.potenziale)}</strong></>);
          if (!pezzi.length) return <span className="mute">Ancora niente fra noi: nessuna fattura, nessun canone, nessuna trattativa aperta.</span>;
          // La prima lettera la fa maiuscola il CSS (.ct2-det-frase::first-letter).
          return <>{pezzi.map((p, i) => <span key={i}>{i === 0 ? '' : i === pezzi.length - 1 ? ' e ' : ', '}{p}</span>)}.</>;
        })()}
      </p>

      {/* ct-detail: solo un aggancio per contacts.css (i bersagli da 44px sui
          recapiti, sul telefono). Sul computer non fa niente. */}
      <div className="tk-detail ct-detail">
        {/* ── COME SI RAGGIUNGE ─────────────────────────────────────────── */}
        <div className="data-hd"><span className="tk-sez-t">Come si raggiunge</span></div>
        <div className="ed-kv">
          <KvRow label="Email">
            {c.recapiti.email
              ? <a className="link" href={`mailto:${c.recapiti.email}`}>{c.recapiti.email}</a>
              : ripiego?.email
                ? <><a className="link" href={`mailto:${ripiego.email}`}>{ripiego.email}</a><span className="mute-2 ml-2 text-[12px]">di {ripiego.fullName}</span></>
                : <span className="mute">Non ce l'abbiamo</span>}
          </KvRow>
          <KvRow label="Telefono">
            {c.recapiti.phone
              ? <a className="link mono" href={`tel:${c.recapiti.phone}`}>{c.recapiti.phone}</a>
              : ripiego?.phone
                ? <><a className="link mono" href={`tel:${ripiego.phone}`}>{ripiego.phone}</a><span className="mute-2 ml-2 text-[12px]">di {ripiego.fullName}</span></>
                : <span className="mute">Non ce l'abbiamo</span>}
          </KvRow>
          {a.pec && <KvRow label="PEC"><span className="mono">{a.pec}</span></KvRow>}
          {a.website && <KvRow label="Sito"><a className="link" href={a.website} target="_blank" rel="noreferrer">{a.website}</a></KvRow>}
          {(a.addressLine || a.city) && (
            <KvRow label="Sede">{[a.addressLine, a.zip, a.city, a.province].filter(Boolean).join(', ')}</KvRow>
          )}
          {a.skills && a.skills.length > 0 && <KvRow label="Fa">{a.skills.join(', ')}</KvRow>}
        </div>

        {/* I referenti: le persone dentro l'azienda. Stanno qui, non schiacciate
            sulla mail dell'azienda, perche' cambiano ruolo e cambiano posto. */}
        {c.referenti.length > 0 && (
          <>
            <div className="data-hd mt-4">
              <span className="tk-sez-t">Persone da sentire</span>
              <span className="mono mute text-[12px]">{c.referenti.length}</span>
            </div>
            {/* tbl--card: sotto i 768px ogni riga diventa una scheda. */}
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {/* m-static: la riga non si apre (un referente non ha una
                    scheda sua), quindi in scheda non deve portare il chevron
                    che vuol dire "si apre". */}
                {c.referenti.map((r) => (
                  <tr key={r.id} className="m-static">
                    <td data-m="hd" className="cell-2l">
                      <div className="p">{r.fullName}{r.isPrimary && <span className="mute-2 ml-2 text-[12px]">principale</span>}</div>
                      {r.role && <div className="s">{r.role}</div>}
                    </td>
                    <td data-m="row" data-l="Email">{r.email ? <a className="link" href={`mailto:${r.email}`}>{r.email}</a> : <span className="mute-2">—</span>}</td>
                    <td data-m="key" data-l="Telefono" className="mono">{r.phone ?? <span className="mute-2">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ── TRATTATIVE ──────────────────────────────────────────────────
            Senza trattative la sezione non c'è (prima: "Trattative 0 · Nessuna
            trattativa."): una scheda corta si legge prima. Il divisore viaggia
            con la sezione, altrimenti restavano due fili di fila. */}
        {c.trattative.length > 0 && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="tk-sez-t">Trattative</span>
              <span className="mono mute text-[12px]">{c.trattative.length}</span>
            </div>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {[...aperte, ...chiuse].map((t) => <RigaTrattativa key={t.id} t={t} onOpen={() => navigate(`/trattative/${t.id}`)} />)}
              </tbody>
            </table>
          </>
        )}

        {/* ── PROGETTI ──────────────────────────────────────────────────── */}
        {c.progetti.length > 0 && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="tk-sez-t">Progetti</span>
              <span className="mono mute text-[12px]">{c.progetti.length}</span>
            </div>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {c.progetti.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                    <td data-m="hd" className="cell-2l">
                      <div className="p">{p.name}</div>
                      <div className="s mono">{p.code}</div>
                    </td>
                    <td data-m="key" data-l="A che punto è"><Chip tone={statusTone(p.status)} dot>{p.status.replace('_', ' ').toLowerCase()}</Chip></td>
                    <td data-m="key" data-l="Consegna">{p.deadline ? fmtDate(p.deadline) : <span className="mute-2">—</span>}</td>
                    <td data-m="key" data-l="Valore" className="num mono">{p.revenue > 0 ? `${euro(p.revenue)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ── ABBONAMENTI ───────────────────────────────────────────────── */}
        {(c.abbonamenti.length > 0 || c.canoni.length > 0) && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="tk-sez-t">Abbonamenti e canoni</span>
              <span className="mono mute text-[12px]">{euro(mensile, true)} al mese</span>
            </div>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {/* m-static: queste righe non si aprono, niente chevron. */}
                {c.abbonamenti.map((s) => (
                  <tr key={s.id} className="m-static">
                    <td data-m="hd">
                      {/* Il logo del prodotto (GoDeal, Aura OS, Hostinger…): prima "stripe · godeal" in minuscolo. */}
                      <div className="ct2-det-abb">
                        <BrandLogo brand={MARCHIO_PRODOTTO[s.type] ?? undefined} name={s.planLabel} size={28} />
                        <span className="cell-2l min-w-0">
                          <span className="p block truncate">{s.planLabel}</span>
                          <span className="s block">{NOME_PRODOTTO[s.type] ?? s.type.toLowerCase()}{s.provider ? ` · ${s.provider === 'STRIPE' ? 'via Stripe' : s.provider.toLowerCase()}` : ''}</span>
                        </span>
                      </div>
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={s.status === 'ACTIVE' ? 'ok' : s.status === 'PAST_DUE' ? 'alert' : 'ghost'} dot>{(SERVICE_STATUS_LABEL as Record<string, string>)[s.status] ?? s.status.toLowerCase()}</Chip></td>
                    {/* Il rinnovo e' la data che serve davvero: dice quando
                        ricapita l'addebito e quando si puo' rinegoziare. */}
                    <td data-m="key" data-l="Rinnovo">{s.currentPeriodEnd ? <>rinnova il <span className="mono">{fmtDate(s.currentPeriodEnd)}</span></> : <span className="mute-2">—</span>}</td>
                    <td data-m="key" data-l="Quanto" className="num mono">{euro(s.amount, true)}<span className="mute-2 ml-1 text-[12px]">/{s.billingCycle === 'YEARLY' ? 'anno' : s.billingCycle === 'QUARTERLY' ? 'trim.' : 'mese'}</span></td>
                  </tr>
                ))}
                {c.canoni.map((r) => (
                  <tr key={r.id} className="m-static">
                    <td data-m="hd" className="cell-2l">
                      <div className="p">{r.name}</div>
                      <div className="s">canone in amministrazione</div>
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={r.inVigore ? 'ghost' : 'info'} dot>{r.inVigore ? 'contratto' : 'non in vigore'}</Chip></td>
                    <td data-m="key" data-l="Rinnovo">
                      {r.inVigore
                        ? <>prossima il <span className="mono">{fmtDate(r.nextDueDate)}</span></>
                        : <span className="mute">fuori dal totale in cima</span>}
                    </td>
                    <td data-m="key" data-l="Quanto" className="num mono">{euro(r.amountNet, true)}<span className="mute-2 ml-1 text-[12px]">/{r.cycle === 'YEARLY' ? 'anno' : r.cycle === 'QUARTERLY' ? 'trim.' : 'mese'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ── FATTURE ───────────────────────────────────────────────────── */}
        {c.fatture.count > 0 && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="tk-sez-t">Fatture</span>
              <span className="mono mute text-[12px]">{c.fatture.count} in tutto</span>
            </div>
            {/* Incassato, da incassare e scaduto non si ripetono qui: li dice la
                frase in cima. Resta solo l'incassato, che lassù non c'è. */}
            {c.fatture.incassato < c.fatture.totale && (
              <div className="ct2-det-nota">Incassati finora {euro(c.fatture.incassato)}.</div>
            )}
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {c.ultimeFatture.map((f) => (
                  <tr key={f.id} onClick={() => navigate(`/invoices/${f.id}`)}>
                    <td data-m="hd" className="cell-2l">
                      <div className="p mono">{f.number ?? 'senza numero'}</div>
                      {f.project?.code && <div className="s">{f.project.code}</div>}
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={statusTone(f.status)} dot>{f.status.toLowerCase()}</Chip></td>
                    <td data-m="key" data-l="Emissione">{fmtDate(f.issueDate)}</td>
                    <td data-m="key" data-l="Totale" className="num mono">{euro(f.total, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {c.fatture.count > c.ultimeFatture.length && (
              <div className="mute text-[12px] mt-2">
                Le ultime {c.ultimeFatture.length}. I totali qui sopra guardano tutte e {c.fatture.count}
                {c.fatture.annullate > 0 ? `, tolte le ${c.fatture.annullate} annullate.` : '.'}
              </div>
            )}
          </>
        )}

        {/* ── ANCHE FORNITORE ───────────────────────────────────────────── */}
        {c.fornitore && (
          <>
            <div className="ed-divider" />
            <div className="data-hd">
              <span className="tk-sez-t">Ci fattura anche lui</span>
              <span className="mono mute text-[12px]">
                {c.fornitore.count} documenti{c.fornitore.annullate > 0 && `, ${c.fornitore.annullate} annullati`}
              </span>
            </div>
            {/* Le due facce del rapporto in una frase: quanto ci ha fatturato,
                quanto resta da pagargli e chi è in vantaggio. */}
            <p className="ct2-det-nota">
              Ci ha fatturato {euro(c.fornitore.totale)}
              {c.fornitore.daPagare > 0 ? <>, e dobbiamo ancora pagargli <strong>{euro(c.fornitore.daPagare)}</strong></> : ', tutto pagato'}.
              {' '}{c.fornitore.saldo >= 0
                ? <>Il saldo è a nostro favore: ci compra più di quanto ci vende ({euro(c.fornitore.saldo)}).</>
                : <>Il saldo è a suo favore: ci vende più di quanto ci compra ({euro(-c.fornitore.saldo)}).</>}
            </p>
            <table className="tbl tbl--ed tbl--card">
              <tbody>
                {/* m-static: le fatture ricevute non hanno una scheda qui. */}
                {c.fornitore.righe.map((p) => (
                  <tr key={p.id} className="m-static">
                    <td data-m="hd" className="cell-2l">
                      <div className="p mono">{p.number ?? 'senza numero'}</div>
                      {p.description && <div className="s">{p.description}</div>}
                    </td>
                    <td data-m="key" data-l="Stato"><Chip tone={statusTone(p.status)} dot>{p.status.replace('_', ' ').toLowerCase()}</Chip></td>
                    <td data-m="key" data-l="Emissione">{fmtDate(p.issueDate)}</td>
                    <td data-m="key" data-l="Totale" className="num mono">{euro(p.total, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2 mt-3">
              {c.fornitore.schede.map((s) => (
                <button key={s.id} className="btn btn-sm btn-ghost" onClick={() => navigate(`/suppliers/${s.id}`)}>
                  Scheda fornitore · {s.fullName}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Telefono: le stesse azioni della cima, dove arriva il pollice. Se non
          ce n'e' nessuna la barra non si monta: una striscia vuota in fondo
          allo schermo e' peggio che niente. */}
      {((canWrite && potenziale) || c.kind === 'client') && (
        <div className="m-actionbar m-only">
          {canWrite && potenziale && (
            <button className="btn btn-primary" disabled={promuovi.isPending} onClick={() => promuovi.mutate()}>
              <Icon name="check" size={12} /> Diventa cliente
            </button>
          )}
          {c.kind === 'client' && (
            <button className="btn" onClick={() => navigate(`/clients/${c.id}`)}>Scheda estesa</button>
          )}
        </div>
      )}
    </div>
  );
}

/** Il marchio e il nome dei nostri prodotti in abbonamento (come in Abbonamenti). */
const MARCHIO_PRODOTTO: Record<string, string | null> = { GODEAL: 'godeal', AURA_OS: 'auraos', HOSTING: 'hostinger', CUSTOM: null };
const NOME_PRODOTTO: Record<string, string> = { GODEAL: 'GoDeal', AURA_OS: 'Aura OS', HOSTING: 'Hosting', CUSTOM: 'Su misura' };

function RigaTrattativa({ t, onOpen }: { t: TrattativaDTO; onOpen: () => void }) {
  return (
    <tr onClick={onOpen}>
      <td data-m="hd" className="cell-2l">
        <div className="p">{t.title}</div>
        <div className="s">{t.pipeline}</div>
      </td>
      <td data-m="key" data-l="Step">
        <Chip tone={t.outcome === 'VINTO' ? 'ok' : t.outcome === 'PERSO' ? 'ghost' : 'info'} dot>{t.stage}</Chip>
      </td>
      {/* 12 e non 12,5: la scala dichiarata del progetto ha cinque corpi, e
          12,5 non e' uno di quelli. */}
      <td data-m="key" data-l="A che punto è">
        {t.projectCode
          ? <span className="mute text-[12px]">è diventata {t.projectCode}</span>
          : t.expectedCloseDate ? <span className="mute text-[12px]">chiusura {fmtDate(t.expectedCloseDate)}</span> : <span className="mute-2">—</span>}
      </td>
      <td data-m="key" data-l="Valore" className="num mono">{euro(t.amount)}</td>
    </tr>
  );
}

function KvRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}{hint && <div className="tk-kv-hint">{hint}</div>}</div>
      <div className="v">{children}</div>
    </div>
  );
}

export type { ContattoCompleto };
